import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  Plus, Users, Phone, MapPin, CreditCard, Monitor,
  Pencil, Trash2, Filter, X, Eye, FileText, RefreshCw, Store, Search, UserPlus, Copy,
  Power, PowerOff, UserX
} from "lucide-react";

const acomodadoSchema = z.object({
  nome: z.string().min(2, "Mínimo 2 caracteres"),
  endereco: z.string().optional().default(""),
  cpf: z.string().optional().default(""),
  telefone: z.string().min(8, "Telefone inválido"),
  quantidade_dispositivos: z.coerce.number().min(1, "Mínimo 1 dispositivo").default(1),
  codigo_launcher: z.string().optional().default(""),
  observacao: z.string().optional().default(""),
});

export default function AcomodadoPage() {
  const { authAxios } = useAuth();
  const [acomodados, setAcomodados] = useState([]);
  const [stats, setStats] = useState({ total_acomodados: 0, total_dispositivos: 0, total_desativados: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingAcomodado, setEditingAcomodado] = useState(null);
  const [viewingAcomodado, setViewingAcomodado] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [filterNome, setFilterNome] = useState("");

  // Revenda Acomodados
  const [revendaAcomodados, setRevendaAcomodados] = useState([]);
  const [revendaStats, setRevendaStats] = useState({ total: 0, total_dispositivos: 0 });
  const [revendaDialogOpen, setRevendaDialogOpen] = useState(false);
  const [editingRevenda, setEditingRevenda] = useState(null);
  const [viewingRevenda, setViewingRevenda] = useState(null);
  const [viewRevendaOpen, setViewRevendaOpen] = useState(false);
  const [filterRevendaNome, setFilterRevendaNome] = useState("");

  const form = useForm({
    resolver: zodResolver(acomodadoSchema),
    defaultValues: {
      nome: "",
      endereco: "",
      cpf: "",
      telefone: "",
      quantidade_dispositivos: 1,
      codigo_launcher: "",
      observacao: "",
    },
  });

  // Listas extras (Códigos Launcher e Nomes TV BOX) - state separado para arrays dinamicos
  const [codigosLauncher, setCodigosLauncher] = useState([]);
  const [nomesTvBox, setNomesTvBox] = useState([]);

  // Funcionarios
  const [funcionarios, setFuncionarios] = useState([]);
  const [dConfigFunc, setDConfigFunc] = useState(false);
  const [novoFunc, setNovoFunc] = useState("");

  const fetchAcomodados = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterNome) params.append("nome", filterNome);
      
      const res = await authAxios.get(`/acomodados?${params.toString()}`);
      setAcomodados(res.data);
    } catch (err) {
      toast.error("Erro ao carregar acomodados");
    } finally {
      setLoading(false);
    }
  }, [authAxios, filterNome]);

  const fetchStats = async () => {
    try {
      const res = await authAxios.get("/acomodados/stats/summary");
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRevendaAcomodados = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterRevendaNome) params.append("nome", filterRevendaNome);
      const res = await authAxios.get(`/rev-acomodados?${params.toString()}`);
      setRevendaAcomodados(res.data);
    } catch { console.error("Erro revenda acomodados"); }
  }, [authAxios, filterRevendaNome]);

  const fetchRevendaStats = async () => {
    try {
      const res = await authAxios.get("/rev-acomodados/stats/summary");
      setRevendaStats(res.data);
    } catch { console.error("Erro stats revenda"); }
  };

  const fetchFuncionarios = async () => {
    try { const res = await authAxios.get("/hub-empresa/funcionarios"); setFuncionarios(res.data); }
    catch { }
  };

  useEffect(() => {
    fetchAcomodados();
    fetchStats();
    fetchRevendaAcomodados();
    fetchRevendaStats();
    fetchFuncionarios();
  }, [fetchAcomodados, fetchRevendaAcomodados]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchAcomodados(), fetchStats(), fetchRevendaAcomodados(), fetchRevendaStats()]);
      toast.success("Dados atualizados!");
    } catch (err) {
      toast.error("Erro ao atualizar");
    } finally {
      setRefreshing(false);
    }
  };

  const handleOpenDialog = (acomodado = null) => {
    if (acomodado) {
      setEditingAcomodado(acomodado);
      form.reset({
        nome: acomodado.nome,
        endereco: acomodado.endereco || "",
        cpf: acomodado.cpf || "",
        telefone: acomodado.telefone,
        quantidade_dispositivos: acomodado.quantidade_dispositivos || 1,
        codigo_launcher: acomodado.codigo_launcher || "",
        observacao: acomodado.observacao || "",
      });
      setCodigosLauncher(Array.isArray(acomodado.codigos_launcher) ? [...acomodado.codigos_launcher] : []);
      setNomesTvBox(Array.isArray(acomodado.nomes_tv_box) ? [...acomodado.nomes_tv_box] : []);
    } else {
      setEditingAcomodado(null);
      form.reset({
        nome: "",
        endereco: "",
        cpf: "",
        telefone: "",
        quantidade_dispositivos: 1,
        codigo_launcher: "",
        observacao: "",
      });
      setCodigosLauncher([]);
      setNomesTvBox([]);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (data) => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        codigos_launcher: codigosLauncher.filter(c => c && c.trim()),
        nomes_tv_box: nomesTvBox.filter(n => n && n.trim()),
      };
      if (editingAcomodado) {
        await authAxios.put(`/acomodados/${editingAcomodado.id}`, payload);
        toast.success("Acomodado atualizado!");
      } else {
        await authAxios.post("/acomodados", payload);
        toast.success("Acomodado cadastrado!");
      }
      setDialogOpen(false);
      fetchAcomodados();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao salvar acomodado");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este acomodado?")) return;
    try {
      await authAxios.delete(`/acomodados/${id}`);
      toast.success("Acomodado excluído!");
      fetchAcomodados();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao excluir acomodado");
    }
  };

  const handleToggleAtivo = async (acomodado) => {
    const isAtivo = acomodado.ativo !== false;
    const acao = isAtivo ? "desativar" : "reativar";
    if (!window.confirm(`Tem certeza que deseja ${acao} este acomodado?`)) return;
    try {
      await authAxios.put(`/acomodados/${acomodado.id}`, { ativo: !isAtivo });
      toast.success(isAtivo ? "Acomodado desativado!" : "Acomodado reativado!");
      fetchAcomodados();
      fetchStats();
    } catch {
      toast.error("Erro ao alterar status");
    }
  };

  const handleView = (acomodado) => {
    setViewingAcomodado(acomodado);
    setViewDialogOpen(true);
  };

  // Revenda Acomodados CRUD
  const revendaForm = useForm({
    resolver: zodResolver(acomodadoSchema),
    defaultValues: { nome: "", endereco: "", cpf: "", telefone: "", quantidade_dispositivos: 1, codigo_launcher: "", observacao: "" },
  });

  const handleOpenRevendaDialog = (item = null) => {
    if (item) {
      setEditingRevenda(item);
      revendaForm.reset({ nome: item.nome, endereco: item.endereco || "", cpf: item.cpf || "", telefone: item.telefone, quantidade_dispositivos: item.quantidade_dispositivos || 1, codigo_launcher: item.codigo_launcher || "", observacao: item.observacao || "" });
    } else {
      setEditingRevenda(null);
      revendaForm.reset({ nome: "", endereco: "", cpf: "", telefone: "", quantidade_dispositivos: 1, codigo_launcher: "", observacao: "" });
    }
    setRevendaDialogOpen(true);
  };

  const handleSubmitRevenda = async (data) => {
    setSubmitting(true);
    try {
      if (editingRevenda) {
        await authAxios.put(`/revenda-acomodados/${editingRevenda.id}`, data);
        toast.success("Revenda acomodado atualizado!");
      } else {
        await authAxios.post("/revenda-acomodados", data);
        toast.success("Revenda acomodado cadastrado!");
      }
      setRevendaDialogOpen(false);
      fetchRevendaAcomodados();
      fetchRevendaStats();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteRevenda = async (id) => {
    if (!window.confirm("Excluir este revenda acomodado?")) return;
    try {
      await authAxios.delete(`/revenda-acomodados/${id}`);
      toast.success("Removido!");
      fetchRevendaAcomodados();
      fetchRevendaStats();
    } catch { toast.error("Erro ao excluir"); }
  };

  const clearFilters = () => {
    setFilterNome("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="acomodado-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Acomodados</h2>
          <p className="text-muted-foreground">Cadastro e organizacao de pessoas acomodadas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </Button>
          <Button onClick={() => handleOpenDialog()} data-testid="new-acomodado-btn"><Plus className="w-4 h-4 mr-1" /> Novo Acomodado</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-3 rounded-lg bg-blue-500/10"><Users className="w-6 h-6 text-blue-500" /></div><div><p className="text-sm text-muted-foreground">Total de Acomodados</p><p className="text-2xl font-bold" data-testid="stat-total-acomodados">{stats.total_acomodados}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-3 rounded-lg bg-emerald-500/10"><Monitor className="w-6 h-6 text-emerald-500" /></div><div><p className="text-sm text-muted-foreground">Total de Dispositivos</p><p className="text-2xl font-bold" data-testid="stat-total-dispositivos">{stats.total_dispositivos}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-3 rounded-lg bg-red-500/10"><UserX className="w-6 h-6 text-red-500" /></div><div><p className="text-sm text-muted-foreground">Total Desativados</p><p className="text-2xl font-bold text-red-400" data-testid="stat-total-desativados">{stats.total_desativados}</p></div></div></CardContent></Card>
      </div>

      <div className="flex items-center gap-1 max-w-xs">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar acomodado por nome..." value={filterNome} onChange={(e) => setFilterNome(e.target.value)} className="pl-10 h-9" data-testid="filter-nome" />
        </div>
        {filterNome && (
          <button className="shrink-0 p-1 rounded text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={clearFilters}>
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <Card>
        <CardContent className="pt-4">
          {acomodados.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground"><Users className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Nenhum acomodado cadastrado</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Nome</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Telefone</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Dispositivos</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Codigo Launcher</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Acoes</th>
                </tr></thead>
                <tbody>{acomodados.map((a) => (
                  <tr key={a.id} className={`border-b border-border/50 hover:bg-muted/50 transition-colors ${a.ativo === false ? "opacity-50" : ""}`}>
                    <td className="py-3 px-4"><div className="flex items-center gap-2"><div className="p-1.5 rounded bg-primary/10"><Users className="w-4 h-4 text-primary" /></div><div><p className="font-medium flex items-center gap-2">{a.nome} {a.ativo === false && <Badge variant="outline" className="text-red-400 border-red-500/40 text-[10px] px-1.5 py-0">DESATIVADO</Badge>}</p>{a.observacao && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{a.observacao}</p>}</div></div></td>
                    <td className="py-3 px-4"><div className="flex items-center gap-2 text-sm"><Phone className="w-3.5 h-3.5 text-muted-foreground" />{a.telefone}</div></td>
                    <td className="py-3 px-4 text-center"><Badge variant="secondary" className="gap-1"><Monitor className="w-3 h-3" />{a.quantidade_dispositivos}</Badge></td>
                    <td className="py-3 px-4"><span className="text-sm font-mono text-blue-400">{a.codigo_launcher || "-"}</span></td>
                    <td className="py-3 px-4"><div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(a)}><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenDialog(a)}><Pencil className="w-4 h-4" /></Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ${a.ativo === false ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"}`}
                        onClick={() => handleToggleAtivo(a)}
                        title={a.ativo === false ? "Reativar" : "Desativar"}
                        data-testid={`toggle-ativo-${a.id}`}
                      >
                        {a.ativo === false ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDelete(a.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAcomodado ? "Editar Acomodado" : "Novo Acomodado"}</DialogTitle>
            <DialogDescription>
              Preencha os dados do acomodado
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="nome">Nome Completo *</Label>
                <Input
                  id="nome"
                  placeholder="Nome completo do acomodado"
                  data-testid="input-acomodado-nome"
                  {...form.register("nome")}
                />
                {form.formState.errors.nome && (
                  <p className="text-sm text-destructive">{form.formState.errors.nome.message}</p>
                )}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="endereco">Endereço</Label>
                <Input
                  id="endereco"
                  placeholder="Endereço completo"
                  data-testid="input-acomodado-endereco"
                  {...form.register("endereco")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf">CPF</Label>
                <Input
                  id="cpf"
                  placeholder="000.000.000-00"
                  data-testid="input-acomodado-cpf"
                  {...form.register("cpf")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone *</Label>
                <Input
                  id="telefone"
                  placeholder="(00) 00000-0000"
                  data-testid="input-acomodado-telefone"
                  {...form.register("telefone")}
                />
                {form.formState.errors.telefone && (
                  <p className="text-sm text-destructive">{form.formState.errors.telefone.message}</p>
                )}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="quantidade_dispositivos">Quantidade de Dispositivos *</Label>
                <Input
                  id="quantidade_dispositivos"
                  type="number"
                  min="1"
                  placeholder="1"
                  data-testid="input-acomodado-dispositivos"
                  {...form.register("quantidade_dispositivos")}
                />
                {form.formState.errors.quantidade_dispositivos && (
                  <p className="text-sm text-destructive">{form.formState.errors.quantidade_dispositivos.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="codigo_launcher">Código Launcher BOX</Label>
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="h-7 px-2 text-primary hover:text-primary"
                    onClick={() => setCodigosLauncher(arr => [...arr, ""])}
                    data-testid="add-codigo-launcher-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
                  </Button>
                </div>
                <Input
                  id="codigo_launcher"
                  placeholder="Código do Launcher BOX"
                  data-testid="input-acomodado-launcher"
                  {...form.register("codigo_launcher")}
                />
                {codigosLauncher.map((c, idx) => (
                  <div key={`cl-${idx}`} className="flex items-center gap-2" data-testid={`codigo-launcher-extra-${idx}`}>
                    <Input
                      placeholder="Código do Launcher BOX"
                      value={c}
                      onChange={e => setCodigosLauncher(arr => {
                        const a = [...arr]; a[idx] = e.target.value; return a;
                      })}
                    />
                    <Button
                      type="button" variant="ghost" size="icon"
                      className="h-9 w-9 shrink-0 text-red-500 hover:text-red-600"
                      onClick={() => setCodigosLauncher(arr => arr.filter((_, i) => i !== idx))}
                      data-testid={`remove-codigo-launcher-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label>Nome TV BOX</Label>
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="h-7 px-2 text-primary hover:text-primary"
                    onClick={() => setNomesTvBox(arr => [...arr, ""])}
                    data-testid="add-nome-tv-box-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
                  </Button>
                </div>
                {nomesTvBox.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">Nenhum nome adicionado. Clique em "Adicionar".</p>
                )}
                {nomesTvBox.map((n, idx) => (
                  <div key={`tv-${idx}`} className="flex items-center gap-2" data-testid={`nome-tv-box-${idx}`}>
                    <Input
                      placeholder="Ex: TV Sala, TV Quarto..."
                      value={n}
                      onChange={e => setNomesTvBox(arr => {
                        const a = [...arr]; a[idx] = e.target.value; return a;
                      })}
                    />
                    <Button
                      type="button" variant="ghost" size="icon"
                      className="h-9 w-9 shrink-0 text-red-500 hover:text-red-600"
                      onClick={() => setNomesTvBox(arr => arr.filter((_, i) => i !== idx))}
                      data-testid={`remove-nome-tv-box-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="observacao">Observação</Label>
                <Textarea
                  id="observacao"
                  placeholder="Observações adicionais sobre o acomodado..."
                  className="min-h-[80px]"
                  data-testid="input-acomodado-observacao"
                  {...form.register("observacao")}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-acomodado-btn">
                {submitting ? "Salvando..." : editingAcomodado ? "Atualizar" : "Cadastrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Acomodado</DialogTitle>
          </DialogHeader>
          
          {viewingAcomodado && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <div className="p-3 rounded-full bg-primary/10">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{viewingAcomodado.nome}</h3>
                  <Badge variant="secondary" className="gap-1 mt-1">
                    <Monitor className="w-3 h-3" />
                    {viewingAcomodado.quantidade_dispositivos} dispositivo(s)
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {viewingAcomodado.telefone && (
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Telefone</p>
                      <p className="font-medium">{viewingAcomodado.telefone}</p>
                    </div>
                  </div>
                )}

                {viewingAcomodado.cpf && (
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <CreditCard className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">CPF</p>
                      <p className="font-medium">{viewingAcomodado.cpf}</p>
                    </div>
                  </div>
                )}

                {viewingAcomodado.endereco && (
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <MapPin className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Endereço</p>
                      <p className="font-medium">{viewingAcomodado.endereco}</p>
                    </div>
                  </div>
                )}

                {(viewingAcomodado.codigo_launcher || (Array.isArray(viewingAcomodado.codigos_launcher) && viewingAcomodado.codigos_launcher.length > 0)) && (
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Códigos Launcher BOX</p>
                    <div className="flex flex-wrap gap-2">
                      {viewingAcomodado.codigo_launcher && (
                        <Badge variant="outline" className="font-mono text-blue-400">{viewingAcomodado.codigo_launcher}</Badge>
                      )}
                      {Array.isArray(viewingAcomodado.codigos_launcher) && viewingAcomodado.codigos_launcher.map((c, i) => (
                        <Badge key={i} variant="outline" className="font-mono text-blue-400">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {Array.isArray(viewingAcomodado.nomes_tv_box) && viewingAcomodado.nomes_tv_box.length > 0 && (
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Nomes TV BOX</p>
                    <div className="flex flex-wrap gap-2">
                      {viewingAcomodado.nomes_tv_box.map((n, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">
                          <Monitor className="w-3 h-3" /> {n}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {viewingAcomodado.observacao && (
                  <div className="p-3 border rounded-lg border-primary/30 bg-primary/5">
                    <div className="flex items-start gap-2">
                      <FileText className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">Observação</p>
                        <p className="font-medium text-foreground">{viewingAcomodado.observacao}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                  Fechar
                </Button>
                <Button onClick={() => {
                  setViewDialogOpen(false);
                  handleOpenDialog(viewingAcomodado);
                }}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Revenda Acomodado Create/Edit */}
      <Dialog open={revendaDialogOpen} onOpenChange={setRevendaDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-revenda-acomodado">
          <DialogHeader>
            <DialogTitle>{editingRevenda ? "Editar" : "Novo"} Revenda Acomodado</DialogTitle>
          </DialogHeader>
          <form onSubmit={revendaForm.handleSubmit(handleSubmitRevenda)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nome *</Label><Input {...revendaForm.register("nome")} placeholder="Nome completo" data-testid="input-revenda-aco-nome" />{revendaForm.formState.errors.nome && <p className="text-sm text-destructive">{revendaForm.formState.errors.nome.message}</p>}</div>
              <div className="space-y-2"><Label>Telefone *</Label><Input {...revendaForm.register("telefone")} placeholder="(00) 00000-0000" data-testid="input-revenda-aco-telefone" />{revendaForm.formState.errors.telefone && <p className="text-sm text-destructive">{revendaForm.formState.errors.telefone.message}</p>}</div>
              <div className="space-y-2"><Label>Endereco</Label><Input {...revendaForm.register("endereco")} placeholder="Endereco completo" /></div>
              <div className="space-y-2"><Label>CPF</Label><Input {...revendaForm.register("cpf")} placeholder="000.000.000-00" /></div>
              <div className="space-y-2"><Label>Dispositivos *</Label><Input type="number" min="1" {...revendaForm.register("quantidade_dispositivos")} /></div>
              <div className="space-y-2"><Label>Codigo Launcher BOX</Label><Input {...revendaForm.register("codigo_launcher")} placeholder="Codigo Launcher" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>Observacao</Label><Textarea {...revendaForm.register("observacao")} placeholder="Observacoes..." /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRevendaDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-revenda-acomodado">{submitting ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: View Revenda Acomodado */}
      <Dialog open={viewRevendaOpen} onOpenChange={setViewRevendaOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-view-revenda-acomodado">
          <DialogHeader><DialogTitle>Detalhes do Revenda Acomodado</DialogTitle></DialogHeader>
          {viewingRevenda && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-muted-foreground">Nome</p><p className="font-medium">{viewingRevenda.nome}</p></div>
                <div><p className="text-sm text-muted-foreground">Telefone</p><p className="font-medium">{viewingRevenda.telefone}</p></div>
                <div><p className="text-sm text-muted-foreground">Endereco</p><p className="font-medium">{viewingRevenda.endereco || "-"}</p></div>
                <div><p className="text-sm text-muted-foreground">CPF</p><p className="font-medium">{viewingRevenda.cpf || "-"}</p></div>
                <div><p className="text-sm text-muted-foreground">Dispositivos</p><p className="font-medium">{viewingRevenda.quantidade_dispositivos}</p></div>
                <div><p className="text-sm text-muted-foreground">Codigo Launcher</p><p className="font-medium font-mono text-blue-400">{viewingRevenda.codigo_launcher || "-"}</p></div>
              </div>
              {viewingRevenda.observacao && <div><p className="text-sm text-muted-foreground">Observacao</p><p className="font-medium">{viewingRevenda.observacao}</p></div>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewRevendaOpen(false)}>Fechar</Button>
                <Button onClick={() => { setViewRevendaOpen(false); handleOpenRevendaDialog(viewingRevenda); }}><Pencil className="w-4 h-4 mr-2" /> Editar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Config Funcionarios */}
      <Dialog open={dConfigFunc} onOpenChange={setDConfigFunc}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Gerenciar Funcionarios</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => { e.preventDefault(); if (!novoFunc.trim()) return; try { await authAxios.post("/hub-empresa/funcionarios", { nome: novoFunc.trim() }); toast.success("Funcionario criado!"); setNovoFunc(""); fetchFuncionarios(); } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); } }} className="flex gap-2">
              <Input value={novoFunc} onChange={(e) => setNovoFunc(e.target.value)} placeholder="Nome do funcionario..." className="flex-1" />
              <Button type="submit" size="sm"><Plus className="w-4 h-4" /></Button>
            </form>
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {funcionarios.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum funcionario cadastrado</p>
              ) : funcionarios.map((f) => (
                <div key={f.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{f.nome}</p>
                    <p className="text-xs text-muted-foreground truncate">{`${window.location.origin}/hub-empresa-func/${f.token}`}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={async () => { const url = `${window.location.origin}/hub-empresa-func/${f.token}`; try { await navigator.clipboard.writeText(url); toast.success("Link copiado!"); } catch { toast.info(url); } }}><Copy className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async () => { if (!window.confirm(`Excluir ${f.nome}?`)) return; try { await authAxios.delete(`/hub-empresa/funcionarios/${f.id}`); toast.success("Removido!"); fetchFuncionarios(); } catch { toast.error("Erro"); } }}><X className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
